import { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from '../context/UserContext';
import { useLocale } from '../context/DynamicLocaleProvider';
import { GlobalInput } from '../components/GlobalInput';
import { GlobalButton } from '../components/GlobalButton';
import { AuthChrome } from '../pages/auth/AuthChrome';
import {
  COLLECTION_ORGANIZATIONS,
  COLLECTION_USERS,
  SUBCOLLECTION_INVITATIONS,
  orgMemberPath,
} from '../lib/firestorePaths';
import type { ClientRole, Invitation } from '../types';
import styles from '../pages/auth/authPages.module.css';

function useMyPendingInvitations(email: string | null) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  useEffect(() => {
    if (!email) {
      setInvitations([]);
      return;
    }
    const q = query(
      collectionGroup(db, SUBCOLLECTION_INVITATIONS),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setInvitations(snap.docs.map((d) => ({ ...(d.data() as Invitation), id: d.id })));
      },
      (error) => {
        console.error('Failed to load invitations:', error);
        setInvitations([]);
      }
    );
    return unsubscribe;
  }, [email]);

  return invitations;
}

function roleKey(role: ClientRole): 'role_client_admin' | 'role_client_user' {
  return role === 'client_admin' ? 'role_client_admin' : 'role_client_user';
}

export function CreateOrgView() {
  const { user, email } = useUser();
  const { t } = useLocale();
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invitations = useMyPendingInvitations(email);

  if (!user) return null;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const legal = legalName.trim() || trimmed;
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const orgRef = doc(collection(db, COLLECTION_ORGANIZATIONS));
      const batch = writeBatch(db);
      batch.set(orgRef, {
        id: orgRef.id,
        name: trimmed,
        legalName: legal,
        status: 'active',
        legalForm: 'GmbH',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        countryCode: 'DE',
        email: (email ?? '').toLowerCase(),
        onboardingStatus: 'complete',
        locale: 'de',
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        version: 1,
      });
      batch.set(doc(db, orgMemberPath(orgRef.id, user.uid)), {
        uid: user.uid,
        orgId: orgRef.id,
        email: (email ?? '').toLowerCase(),
        displayName: user.displayName ?? '',
        createdAt: serverTimestamp(),
      });
      // The single role key lives on users/{uid}; rules validate the grant
      // against the org's createdBy in the same batch.
      batch.set(
        doc(db, COLLECTION_USERS, user.uid),
        { orgId: orgRef.id, role: 'client_admin' },
        { merge: true }
      );
      await batch.commit();
    } catch (err) {
      console.error('Failed to create company:', err);
      setError(t('create_org_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvitation = async (invitation: Invitation, accept: boolean) => {
    if (busyInvitationId) return;
    setBusyInvitationId(invitation.id);
    setError(null);
    try {
      if (accept) {
        const batch = writeBatch(db);
        batch.set(doc(db, orgMemberPath(invitation.orgId, user.uid)), {
          uid: user.uid,
          orgId: invitation.orgId,
          email: (email ?? '').toLowerCase(),
          displayName: user.displayName ?? '',
          invitationId: invitation.id,
          createdAt: serverTimestamp(),
        });
        batch.update(
          doc(db, COLLECTION_ORGANIZATIONS, invitation.orgId, SUBCOLLECTION_INVITATIONS, invitation.id),
          { status: 'accepted' }
        );
        // Role grant validated by rules against the pending invitation.
        batch.set(
          doc(db, COLLECTION_USERS, user.uid),
          { orgId: invitation.orgId, role: invitation.role, invitationId: invitation.id },
          { merge: true }
        );
        await batch.commit();
      } else {
        await updateDoc(
          doc(db, COLLECTION_ORGANIZATIONS, invitation.orgId, SUBCOLLECTION_INVITATIONS, invitation.id),
          { status: 'declined' }
        );
      }
    } catch (err) {
      console.error('Failed to process invitation:', err);
      setError(t('invites_error'));
    } finally {
      setBusyInvitationId(null);
    }
  };

  return (
    <AuthChrome>
      <div className={styles.card}>
        {invitations.length > 0 ? (
          <section style={{ marginBottom: 24 }}>
            <h1 className={styles.title}>{t('invites_title')}</h1>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 12 }}>
              {invitations.map((inv) => (
                <li key={inv.id} style={{ border: '1px solid var(--border, #ddd)', borderRadius: 8, padding: 12 }}>
                  <strong>{inv.orgName}</strong>
                  <div className="muted">
                    {t('invites_role_label')}: {t(roleKey(inv.role))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <GlobalButton
                      type="button"
                      disabled={busyInvitationId === inv.id}
                      onClick={() => handleInvitation(inv, true)}
                    >
                      {t('invites_accept')}
                    </GlobalButton>
                    <GlobalButton
                      type="button"
                      variant="secondary"
                      disabled={busyInvitationId === inv.id}
                      onClick={() => handleInvitation(inv, false)}
                    >
                      {t('invites_decline')}
                    </GlobalButton>
                  </div>
                </li>
              ))}
            </ul>
            <p className="muted">{t('create_org_or')}</p>
          </section>
        ) : null}

        <h1 className={styles.title}>{t('create_org_title')}</h1>
        <p className={styles.subtitle}>{t('create_org_subtitle')}</p>
        <form onSubmit={handleCreate} className={styles.form}>
          <GlobalInput
            label={t('create_org_name_label')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <GlobalInput
            label={t('create_org_legal_name_label')}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
          {error ? <p className="form-error">{error}</p> : null}
          <GlobalButton type="submit" disabled={submitting || !name.trim()}>
            {t('create_org_submit')}
          </GlobalButton>
        </form>
      </div>
    </AuthChrome>
  );
}
